/**
 * Polymarket Book Normalizer — Bridge from probability to cents
 *
 * Converts Polymarket CLOB orderbook data (probability 0.0..1.0) into the
 * internal BookLevel format (priceCents 0..100) used by all NBA/sports modules.
 *
 * @ai-context
 *   Role: Unit boundary between Polymarket (probability) and internal (cents).
 *         All Polymarket book data flows through these two functions.
 *   Pattern: Pure array transforms with validation filters.
 *   Purity: No side effects, no API calls.
 *
 * @ai-invariant Polymarket prices are probability (0.0..1.0). This module
 *   multiplies by 100 to get cents. Every downstream module expects cents.
 *   If this conversion is wrong, ALL Polymarket pricing is wrong.
 *
 * @ai-invariant polyAsksAsNoLevels inverts YES asks into NO bids:
 *   NO_bid_cents = 100 - (YES_ask * 100). This matches the internal
 *   convention where both yes[] and no[] arrays are bid ladders.
 *
 * @ai-upstream
 *   - @/lib/nbaConsolidated/types → BookLevel (priceCents, size)
 *   - ./marketStream → PolyBookLevel (price as string 0..1)
 *
 * @ai-downstream
 *   - sportsStream/stream.ts → polyBidsAsYesLevels, polyAsksAsNoLevels
 *   - stream.ts → polyBidsAsYesLevels, polyAsksAsNoLevels
 *   - normalize.test.ts (2 cases — minimal coverage)
 */

import type { BookLevel } from '@/lib/nbaConsolidated/types';
import type { PolyBookLevel } from './marketStream';

export function polyBidsAsYesLevels(bids: PolyBookLevel[]): BookLevel[] {
  return (
    bids
      // @ai-danger: Polymarket price is string "0.XX" (probability). * 100 = cents.
      //   If this conversion is wrong, all Poly pricing is 100x off.
      .map((lvl) => ({ priceCents: Math.round(Number(lvl.price) * 100), size: Number(lvl.size) }))
      .filter(
        (x) =>
          Number.isFinite(x.priceCents) &&
          x.priceCents > 0 &&
          x.priceCents < 100 &&
          Number.isFinite(x.size) &&
          x.size > 0
      )
      .sort((a, b) => b.priceCents - a.priceCents)
  );
}

/**
 * Polymarket CLOB book is for the token (YES outcome). To match the `nba/` UI convention
 * where both `yes` and `no` are **bid ladders**, we invert the YES asks into NO bids:
 * \(NO_bid_cents = 100 - YES_ask_cents\).
 */
export function polyAsksAsNoLevels(asks: PolyBookLevel[]): BookLevel[] {
  return (
    asks
      // @ai-danger: Double conversion — probability * 100 = cents, then 100 - cents = NO bid.
      //   This matches the internal convention where no[] arrays are bid ladders.
      .map((lvl) => ({
        priceCents: 100 - Math.round(Number(lvl.price) * 100),
        size: Number(lvl.size),
      }))
      .filter(
        (x) =>
          Number.isFinite(x.priceCents) &&
          x.priceCents > 0 &&
          x.priceCents < 100 &&
          Number.isFinite(x.size) &&
          x.size > 0
      )
      .sort((a, b) => b.priceCents - a.priceCents)
  );
}

/**
 * @ai-modification-checklist
 *
 * Before modifying this file, verify:
 *
 * 1. UNIT BRIDGE — Polymarket prices are probability (0.0..1.0), stored as
 *    strings. This module * 100 → cents. ALL downstream expects cents.
 *
 * 2. NO BID INVERSION — polyAsksAsNoLevels: NO_bid = 100 - (ask * 100).
 *    Matches the internal convention that both yes[] and no[] are bid ladders.
 *
 * 3. VALIDATION FILTER — Rejects priceCents <= 0, >= 100, non-finite, and
 *    size <= 0. This silently drops bad levels — not logged.
 *
 * 4. SORT ORDER — Both functions sort descending by priceCents (best first).
 *    Consumers assume descending order.
 *
 * 5. TEST COVERAGE — normalize.test.ts has only 2 cases. High-value gap.
 */
